import { type IanaTimeZoneName, TIMEZONES } from "./timezone-list";

export interface TimezoneOption {
  id: string;
  value: string;
  text: string;
  offset: string;
}

export interface FriendlyTimezoneOption<TValue extends string = string> {
  id: TValue;
  value: TValue;
  text: string;
  searchText: string;
}

// Common timezones as fallback if Intl.supportedValuesOf isn't available
const COMMON_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Madrid",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Australia/Sydney",
  "Australia/Melbourne",
];

const PREFERRED_FRIENDLY_TIMEZONE_VALUES: readonly IanaTimeZoneName[] = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Auckland",
];

// Intl.supportedValuesOf is not available in all browsers, so we use a fallback
export function getAvailableTimezones(): string[] {
  try {
    if ("supportedValuesOf" in Intl) {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch (error) {
    console.warn(
      "Intl.supportedValuesOf not available, using fallback timezones",
    );
  }
  return COMMON_TIMEZONES;
}

export function isTimezoneSupported(timezone: string): boolean {
  try {
    Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

export function getTimezoneWithOffset(timezone: string): TimezoneOption {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "shortOffset",
  });
  const parts = formatter.formatToParts(now);
  const offset =
    parts.find((part) => part.type === "timeZoneName")?.value || "";

  const formattedOffset = offset.replace(
    /GMT([+-])(\d+)(?::(\d+))?/,
    (_, sign, hours, minutes) => {
      const paddedHours = hours.padStart(2, "0");
      const paddedMinutes = (minutes || "00").padStart(2, "0");
      return `GMT${sign}${paddedHours}:${paddedMinutes}`;
    },
  );

  return {
    id: timezone,
    value: timezone,
    text: `(${formattedOffset}) ${timezone.replace(/_/g, " ")}`,
    offset,
  };
}

export function getFriendlyTimezoneOptions(): FriendlyTimezoneOption<IanaTimeZoneName>[] {
  const seen = new Set<string>();
  const options: FriendlyTimezoneOption<IanaTimeZoneName>[] = [];

  for (const timeZoneGroup of TIMEZONES) {
    const value = getPreferredTimezoneValue(timeZoneGroup.utc);
    if (!value || seen.has(value)) {
      continue;
    }

    seen.add(value);
    options.push(getFriendlyTimezoneOption(value));
  }

  return options;
}

export function getFriendlyTimezoneOption<TValue extends string>(
  timezone: TValue,
): FriendlyTimezoneOption<TValue> {
  const timeZoneGroup = TIMEZONES.find((candidate) =>
    isTimezoneInGroup(candidate.utc, timezone),
  );
  const label = timeZoneGroup
    ? getFriendlyTimezoneGroupLabel(timeZoneGroup.text)
    : formatTimezoneDisplay(timezone);
  const formattedOffset = getFormattedTimezoneOffset(timezone);
  const text = formattedOffset ? `(${formattedOffset}) ${label}` : label;
  const searchText = [
    text,
    timezone,
    timeZoneGroup?.value,
    timeZoneGroup?.abbr,
    timeZoneGroup?.text,
    ...(timeZoneGroup?.utc ?? []),
  ]
    .filter(Boolean)
    .join(" ");

  return { id: timezone, value: timezone, text, searchText };
}

export function filterFriendlyTimezoneOptions<
  TOption extends { text: string; value: string; searchText?: string },
>(options: readonly TOption[], searchString: string): TOption[] {
  const normalizedSearch = normalizeTimezoneSearch(searchString);
  if (!normalizedSearch) {
    return [...options];
  }

  return options.filter((option) =>
    normalizeTimezoneSearch(
      [option.text, option.value, option.searchText].filter(Boolean).join(" "),
    ).includes(normalizedSearch),
  );
}

export function getTimezoneOffset(timezone: string): string {
  return getTimezoneWithOffset(timezone).offset;
}

export function formatTimezoneDisplay(timezone: string): string {
  return timezone.replace(/_/g, " ");
}

export function removeFormattedTimezoneOffsetPrefix(
  timeZoneText: string,
): string {
  // Friendly timezone labels can start with "(GMT)" or "(GMT-6:00)";
  // compact surfaces strip that prefix while dropdown rows keep it for disambiguation.
  return timeZoneText.replace(/^\(GMT(?:[+-]\d{1,2}:\d{2})?\)\s*/, "");
}

function getPreferredTimezoneValue(
  timezones: readonly IanaTimeZoneName[],
): IanaTimeZoneName | null {
  for (const preferredTimezone of PREFERRED_FRIENDLY_TIMEZONE_VALUES) {
    if (
      timezones.includes(preferredTimezone) &&
      isTimezoneSupported(preferredTimezone)
    ) {
      return preferredTimezone;
    }
  }

  return timezones.find(isTimezoneSupported) ?? null;
}

function getFriendlyTimezoneGroupLabel(timezoneGroupText: string): string {
  return timezoneGroupText
    .replace(/^\(UTC(?:[+-]\d{2}:\d{2})?\)\s*/, "")
    .replace(/\bStandard Time\b/g, "Time")
    .replace(/\bDaylight Time\b/g, "Time")
    .replace(/\s+/g, " ")
    .trim();
}

function isTimezoneInGroup(
  timezones: readonly string[],
  timezone: string,
): boolean {
  return timezones.includes(timezone);
}

function getFormattedTimezoneOffset(timezone: string): string | null {
  if (!isTimezoneSupported(timezone)) {
    return null;
  }

  return formatTimezoneOffset(getTimezoneWithOffset(timezone).offset);
}

function formatTimezoneOffset(offset: string): string {
  return offset.replace(
    /GMT([+-])(\d+)(?::(\d+))?/,
    (_, sign, hours, minutes) => {
      const paddedHours = hours.padStart(2, "0");
      const paddedMinutes = (minutes || "00").padStart(2, "0");
      return `GMT${sign}${paddedHours}:${paddedMinutes}`;
    },
  );
}

function normalizeTimezoneSearch(value: string): string {
  return value.toLowerCase().replace(/[_/\s()[\].,'+-]/g, "");
}
